import { useCallback, useMemo, useState } from 'react';
import { useDietHistory } from '../../api/residentsApi';
import { useAimDietRun, type AimDietRunResponse, type AimDietFlucsInput } from '../../api/aimDietApi';
import type { DietOrderActionResult, HeldDietOrder } from '../../api/dietOrderLockApi';
import { useGlobalStore } from '../../stores/globalStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useSecurityVisibility } from '../../hooks/useSecurityVisibility';
import HeldEhrChangesDialog from './HeldEhrChangesDialog';
import AimDietUpdateDialog from './AimDietUpdateDialog';
import { useSmAimChain } from './useSmAimChain';
import { currentDietFluc } from './dietOrderProvenance';

interface Props {
  /** Shows the review dialog. The AIM dialogs that follow a save run whether or not it is still open. */
  open: boolean;
  onClose: () => void;
  facilityId: number;
  patientId: number;
  /** For the Selective Menu AIM leg's messages. */
  patientFullName: string;
  /** The resident's held orders, oldest first. */
  held: HeldDietOrder[];
}

/**
 * HeldEhrChangesDialog plus what follows a save, for pages that are not
 * the Diet History tab (the Dashboard card and the Meal Pattern notice):
 * the result toasts, and AIM when the resident's current diet changed
 * (WinForms runs AIM after "Use the New Diet Order" from the Meal
 * Pattern cards and after Apply Pending Diet on the dashboard).
 *
 * Keep this mounted while AIM may run: a save can close the review
 * dialog (last held order) and the AIM dialogs open after that.
 */
export default function HeldEhrChangesReview({
  open,
  onClose,
  facilityId,
  patientId,
  patientFullName,
  held,
}: Props) {
  const ehrTitle = useGlobalStore((s) => s.ehrTitleLabel) || 'EHR';
  const residentLabel = useGlobalStore((s) => s.residentLabel);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { canEditDietFluctuations } = useSecurityVisibility();

  // The dialog shows the current diet order next to the held ones; same
  // source and rule as the Diet History tab.
  const { data: history = [] } = useDietHistory(facilityId, patientId);
  const currentDietOrder = useMemo(() => currentDietFluc(history)?.dietOrder ?? '', [history]);

  const aimDietRun = useAimDietRun();
  const [aimResult, setAimResult] = useState<AimDietRunResponse | null>(null);
  const [aimLoading, setAimLoading] = useState(false);
  const { armSmAim, runSmAim, smDialog } = useSmAimChain({ facilityId, patientId, patientFullName });

  const runAim = useCallback((before: AimDietFlucsInput, after: AimDietFlucsInput) => {
    armSmAim(before, after);
    setAimLoading(true);
    aimDietRun
      .mutateAsync({
        facilityId,
        patientId,
        rowDietFlucsOriginal: before,
        rowDietFlucsModified: after,
        source: 'dietFlucsGrid',
      })
      .then((aimRes) => {
        setAimLoading(false);
        if (aimRes.hasProposals) setAimResult(aimRes);
        else runSmAim();
      })
      .catch(() => { setAimLoading(false); });
  }, [armSmAim, aimDietRun, facilityId, patientId, runSmAim]);

  const handleResolved = useCallback((res: DietOrderActionResult) => {
    if (res?.toastMessage) {
      addNotification({
        message: res.toastMessage,
        type: (res.toastType || 'info') as 'success' | 'warning' | 'info',
        duration: 6000,
      });
    }
    if (res?.aimBefore && res?.aimAfter) runAim(res.aimBefore, res.aimAfter);
  }, [addNotification, runAim]);

  return (
    <>
      <HeldEhrChangesDialog
        open={open}
        onClose={onClose}
        facilityId={facilityId}
        patientId={patientId}
        held={held}
        currentDietOrder={currentDietOrder}
        ehrTitle={ehrTitle}
        residentLabel={residentLabel}
        canEdit={canEditDietFluctuations}
        onResolved={handleResolved}
      />
      <AimDietUpdateDialog
        open={aimLoading || aimResult !== null}
        loading={aimLoading}
        facilityId={facilityId}
        patientId={patientId}
        result={aimResult}
        residentFullName={patientFullName}
        onClose={() => {
          setAimResult(null);
          setAimLoading(false);
          runSmAim();
        }}
      />
      {smDialog}
    </>
  );
}
